import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as Sentry from '@sentry/react-native';
import { router } from 'expo-router';

import { useTranslation } from '../hooks/useTranslation';
import { ROUTES } from '../navigation/routes';
import { brand } from '../design-system/theme/tokens';

/**
 * Error boundaries (de-slop FE-22).
 *
 * - scope "screen": wraps one screen's content (WebAppShell, route-level
 *   `ErrorBoundary` exports). The navigation shell keeps working, and
 *   "Try again" remounts the screen instead of re-rendering the same broken
 *   tree in place.
 * - scope "app": the outermost boundary in app/_layout.tsx.
 *
 * The fallback never shows the raw error message (it can contain user
 * content); the error goes to Sentry.
 */
export type ErrorBoundaryScope = 'app' | 'screen';

interface Props {
  children: React.ReactNode;
  scope?: ErrorBoundaryScope;
  /** When this changes (e.g. the active screen), a shown error is cleared. */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
  attempt: number;
  resetKey?: unknown;
}

export function reloadApp() {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.reload();
  }
}

export function ErrorFallback({ scope, onRetry }: { scope: ErrorBoundaryScope; onRetry?: () => void }) {
  const { t } = useTranslation();
  const isWeb = Platform.OS === 'web';
  const showDashboard = scope === 'screen';
  return (
    <View style={styles.container} accessibilityRole="alert" testID={`error-boundary-${scope}`}>
      <MaterialIcons name="error-outline" size={56} color="#B42318" />
      <Text style={styles.title}>{t('errorBoundary.title')}</Text>
      <Text style={styles.body}>
        {scope === 'screen'
          ? t('errorBoundary.screenBody')
          : t('errorBoundary.appBody')}
      </Text>
      <View style={styles.actions}>
        {onRetry && (
          <Pressable style={[styles.button, styles.primary]} onPress={onRetry} accessibilityRole="button" testID="error-boundary-retry">
            <Text style={styles.primaryText}>{t('errorBoundary.tryAgain')}</Text>
          </Pressable>
        )}
        {showDashboard && (
          <Pressable
            style={[styles.button, styles.secondary]}
            onPress={() => router.replace(ROUTES.TABS_DASHBOARD as any)}
            accessibilityRole="button"
            testID="error-boundary-dashboard"
          >
            <Text style={styles.secondaryText}>{t('errorBoundary.goToDashboard')}</Text>
          </Pressable>
        )}
        {isWeb && (
          <Pressable style={[styles.button, styles.secondary]} onPress={reloadApp} accessibilityRole="button" testID="error-boundary-reload">
            <Text style={styles.secondaryText}>{t('errorBoundary.reload')}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, attempt: 0, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey, hasError: false };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Metadata only in the console (GDPR); the full error goes to Sentry.
    console.error('Error caught by boundary:', { scope: this.props.scope ?? 'app', error_type: error?.name });
    Sentry.captureException(error, {
      tags: { error_boundary: this.props.scope ?? 'app' },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  private retry = () => {
    // A new key remounts the subtree, so the screen re-runs its effects and
    // data loading instead of re-rendering the same broken state.
    this.setState((prev) => ({ hasError: false, attempt: prev.attempt + 1 }));
  };

  render() {
    const scope = this.props.scope ?? 'app';
    if (this.state.hasError) {
      return <ErrorFallback scope={scope} onRetry={this.retry} />;
    }

    return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 320,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
    backgroundColor: '#F8FAFC',
  },
  title: {
    color: '#1E293B',
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  body: {
    color: '#475569',
    fontSize: 16,
    lineHeight: 23,
    textAlign: 'center',
    maxWidth: 440,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginTop: 8,
  },
  button: {
    minHeight: 44,
    paddingHorizontal: 18,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primary: {
    backgroundColor: brand.primary,
  },
  primaryText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  secondary: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
  },
  secondaryText: {
    color: '#1E293B',
    fontSize: 16,
    fontWeight: '700',
  },
});

export default ErrorBoundary;
